import { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { AdminHome, AdminHomeDetail, AdminTableColumn, AdminTableAction } from '../../../types/admin';
import AdminTable from '../../admin/shared/AdminTable';
import AdminConfirmModal from '../../admin/shared/AdminConfirmModal';
import HomeDetailModal from './HomeDetailModal';
import FlagModal from './FlagModal';
import { format } from 'date-fns';

interface HomesModerationProps {
  homes: AdminHome[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onFilterStatus: (status: string) => void;
  onFilterFlagged: (flagged: boolean | undefined) => void;
  onFlagHome: (homeId: string, reason: string) => Promise<{ success: boolean; error?: string }>;
  onUnflagHome: (homeId: string) => Promise<{ success: boolean; error?: string }>;
  onDeleteHome: (homeId: string) => Promise<{ success: boolean; error?: string }>;
  onViewHomeDetail: (homeId: string) => AdminHomeDetail | null;
}

/**
 * HomesModeration - Homes moderation tab
 *
 * Features:
 * - Search and filter homes
 * - View home details with images
 * - Flag/unflag homes
 * - Delete homes
 */
export default function HomesModeration({
  homes,
  isLoading,
  onSearch,
  onFilterStatus,
  onFilterFlagged,
  onFlagHome,
  onUnflagHome,
  onDeleteHome,
  onViewHomeDetail,
}: HomesModerationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flaggedFilter, setFlaggedFilter] = useState('all');
  const [selectedHome, setSelectedHome] = useState<AdminHomeDetail | null>(null);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [homeToFlag, setHomeToFlag] = useState<AdminHome | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [homeToDelete, setHomeToDelete] = useState<AdminHome | null>(null);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    onFilterStatus(status === 'all' ? '' : status);
  };

  const handleFlaggedFilter = (flagged: string) => {
    setFlaggedFilter(flagged);
    onFilterFlagged(flagged === 'all' ? undefined : flagged === 'flagged');
  };

  const handleViewHome = (home: AdminHome) => {
    const detail = onViewHomeDetail(home.id);
    setSelectedHome(detail);
  };

  const handleOpenFlagModal = (home: AdminHome) => {
    setHomeToFlag(home);
    setFlagModalOpen(true);
  };

  const handleFlag = async (reason: string, _customReason?: string) => {
    if (homeToFlag) {
      await onFlagHome(homeToFlag.id, reason);
      setFlagModalOpen(false);
      setHomeToFlag(null);
    }
    return { success: true };
  };

  const handleUnflag = async (home: AdminHome) => {
    await onUnflagHome(home.id);
  };

  const handleOpenDeleteModal = (home: AdminHome) => {
    setHomeToDelete(home);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (homeToDelete) {
      await onDeleteHome(homeToDelete.id);
      setDeleteModalOpen(false);
      setHomeToDelete(null);
    }
  };

  const columns: AdminTableColumn<AdminHome>[] = [
    {
      key: 'preview_image',
      label: 'Preview',
      width: 80,
      render: (home) => (
        <Avatar
          src={home.preview_image}
          variant="rounded"
          sx={{ width: 56, height: 56 }}
        >
          {home.name.charAt(0)}
        </Avatar>
      ),
    },
    {
      key: 'name',
      label: 'Property Name',
      render: (home) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {home.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {home.address}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'owner_name',
      label: 'Owner',
      render: (home) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {home.owner_picture && (
            <Avatar src={home.owner_picture} sx={{ width: 32, height: 32 }} />
          )}
          <Box>
            <Typography variant="body2">{home.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {home.owner_email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (home) => (
        <Typography variant="body2">
          {format(new Date(home.created_at), 'MMM d, yyyy')}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (home) => (
        <Chip
          label={home.status}
          color={home.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      key: 'flagged',
      label: 'Flagged',
      render: (home) => (
        home.flagged ? (
          <Tooltip title={home.flag_reason || 'Flagged'}>
            <Chip
              label="Flagged"
              color="error"
              size="small"
              icon={<FlagIcon />}
            />
          </Tooltip>
        ) : (
          <Chip label="Clean" color="default" size="small" variant="outlined" />
        )
      ),
    },
    {
      key: 'images_count',
      label: 'Images',
      render: (home) => (
        <Typography variant="body2">{home.images_count}</Typography>
      ),
    },
  ];

  const actions: AdminTableAction<AdminHome>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: handleViewHome,
      color: 'primary',
    },
    {
      label: 'Flag',
      icon: <FlagOutlinedIcon />,
      onClick: handleOpenFlagModal,
      color: 'warning',
      disabled: (home) => home.flagged,
    },
    {
      label: 'Unflag',
      icon: <FlagIcon />,
      onClick: handleUnflag,
      color: 'success',
      disabled: (home) => !home.flagged,
    },
    {
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: handleOpenDeleteModal,
      color: 'error',
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Search homes, owners, addresses..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Flagged Status</InputLabel>
            <Select
              value={flaggedFilter}
              label="Flagged Status"
              onChange={(e) => handleFlaggedFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="flagged">Flagged Only</MenuItem>
              <MenuItem value="clean">Clean Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {homes.length} homes
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <AdminTable
        columns={columns}
        data={homes}
        actions={actions}
        isLoading={isLoading}
      />

      {/* Home Detail Modal */}
      <HomeDetailModal
        open={!!selectedHome}
        home={selectedHome}
        onClose={() => setSelectedHome(null)}
      />

      {/* Flag Modal */}
      <FlagModal
        open={flagModalOpen}
        itemType="home"
        itemName={homeToFlag?.name || ''}
        onClose={() => {
          setFlagModalOpen(false);
          setHomeToFlag(null);
        }}
        onFlag={handleFlag}
      />

      {/* Delete Confirmation Modal */}
      <AdminConfirmModal
        open={deleteModalOpen}
        title="Delete Home"
        message={`Are you sure you want to delete "${homeToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        severity="error"
        consequences={[
          'All property images will be permanently deleted',
          'Owner will lose access to this home',
          'All associated data will be removed',
        ]}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setHomeToDelete(null);
        }}
      />
    </Box>
  );
}
